import React, { useEffect, useState } from 'react';
import api from '../../api';
import DeviceDetail from '../../components/deviceDetails/DeviceDetails';
import ErrorMessage from '../../components/errorMessage/ErrorMessage';
import Loader from '../../components/loader/Loader';
import { deviceStore, generalStore } from '../../store';
import { type CollectionResponse } from '../../api/types/response';
import { type Playlist, type ModuleType, type Device } from '../../api/types/index';
import { useLocation, useParams } from 'react-router';
import { RESPONSE } from '../../js/_constants';
import queryString from 'query-string';
import GoBackButton from '../../components/goBackButton/GoBackButton';
import './DeviceDetailsView.scss';
import { useModuleTypesStore } from '../../store';
import {isEmpty} from 'ramda';

const DeviceDetailsView = () => {
    const location = useLocation();
    const { id } = useParams<{ id: string }>();
    const [fallbackPlaylist, setFallbackPlaylist] = useState<
        CollectionResponse<Playlist> | undefined
    >(undefined);

    const { moduleTypes } = useModuleTypesStore();
    const { isGlobalLoading } = generalStore()

    const { updateDevice, selected, loading, error, getDevice } = deviceStore();

    const [isSaving, setIsSaving] = useState(false);
    const isInDevicePlaylists = (playlistIri: string) =>
        selected.playlists.findIndex(p => p.contentPlaylist[RESPONSE.ID] === playlistIri) > -1;

    const isInDeviceGroup = (groupIri: string) =>
    selected.playlistGroups.findIndex(p => p[RESPONSE.ID] === groupIri) > -1;

    useEffect(() => {
        if (!isGlobalLoading) {
            const query = queryString.parse(location.search)
            if (id) {
                //@ts-ignore
                getDevice(id, query.brand);
            }
    }
    }, [id, location.search, isGlobalLoading]);

    /**
     * Locally manage loading of saving, because otherwise
     * if playlist overlay is open and devices loading from store is used
     * it re-renders all children and the overlay closes. We dont want it.
     */
    useEffect(() => {
        if (isSaving) setIsSaving(false);
    }, [selected]);

    const [isFallbackLoading, setIsFallbackLoading] = useState(true);
    const fetchFallbackPlaylist = async countryIso3 => {
        setIsFallbackLoading(true);
        const response = await api.getFallbackPlaylist(countryIso3);
        if (response[RESPONSE.EXPAND_COLLECTION].length > 0) {
            setFallbackPlaylist(response);
        } else {
            const response = await api.getGlobalFallbackPlaylist();
            setFallbackPlaylist(response);
        }
        setIsFallbackLoading(false);
    };

    useEffect(() => {
        if (selected && !fallbackPlaylist && selected.dealership?.country) {
            fetchFallbackPlaylist(selected.dealership.country.split('/')[2]);
        }
    }, [selected]);

    const handleSaving = async updatedDeviceDetails => {
        setIsSaving(true);
        const body = { ...updatedDeviceDetails };
        updateDevice({ id: selected[RESPONSE.ID], body });
    };

    const handleTogglePlaylistAssignment = (playlistIri: string) => {
        let nextPlaylists;

        if (isInDevicePlaylists(playlistIri)) {
            nextPlaylists = selected.playlists
                .map(playlist => { return { position: playlist.position, contentPlaylist: playlist.contentPlaylist[RESPONSE.ID] }})
                .filter(devicePlaylist => devicePlaylist.contentPlaylist !== playlistIri);
        } else {
            nextPlaylists = [
                ...selected.playlists.map(playlist => { return { position: playlist.position,contentPlaylist: playlist.contentPlaylist[RESPONSE.ID] }}),
                {position: selected.playlists.length + 1, contentPlaylist: playlistIri}
            ];
        }

        updateDevice({
            id: selected[RESPONSE.ID],
            body: {
                playlists: nextPlaylists
            }
        });
    };

    const handleToggleGroupAssignment = (groupIri: string) => {
        updateDevice({
            id: selected[RESPONSE.ID],
            body: {
                playlistGroups: [groupIri]
            }
        });
    };

    if (isEmpty(selected) || loading) {
        return <Loader />;
    }

    return (
        <div className="b-devices-detail-view">
            <div className='back-button-wrapper'>
                <GoBackButton />
            </div>            
            {error ? (
                <ErrorMessage message={error} />
            ) : (
                <DeviceDetail
                    data={selected}
                    onSave={handleSaving}
                    onPlaylistAssignment={handleTogglePlaylistAssignment}
                    onGroupAssignment={handleToggleGroupAssignment}
                    fallbackPlaylist={
                        fallbackPlaylist as CollectionResponse<Playlist>
                    }
                    isFallbackLoading={isFallbackLoading}
                    moduleTypes={moduleTypes}
                    isSaving={isSaving}
                    isLoading={loading}
                />
            )}
        </div>
    );
};

export default DeviceDetailsView;